// @flow
/* global document */
import * as React from 'react';

import { ArrowLeftRight, Check, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { selectCartUUID } from '@gandi/cart';
import { Select, Label } from '@gandi/form';
import { useTranslator } from '@gandi/translate';
import { Hide, SrOnly } from '@gandi/ui';
import {
  SegmentedControl,
  SegmentedControlItem,
} from '@gandi/ui/segmentedcontrol';

import { useAppSelector } from 'src/hooks/useAppSelector';

type Props = {
  process: 'create' | 'renew' | 'transfer',
};

function DomainProcessNavSwitch({ process }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const navigate = useNavigate();
  const cartId = useAppSelector(selectCartUUID);

  const label = __('Domain');
  const items = [
    { value: 'create', text: __('Register'), icon: <Check /> },
    { value: 'renew', text: __('Renew'), icon: <RefreshCcw /> },
    { value: 'transfer', text: __('Transfer'), icon: <ArrowLeftRight /> },
  ];
  const values = items.map(({ value, text }) => ({ value, text }));

  React.useEffect(() => {
    const options = document?.querySelectorAll(
      '#domain-process-nav-switch option',
    );

    if (!options) return;

    function onClick(e: MouseEvent) {
      // $FlowFixMe[prop-missing]
      if (process === e.target.value) {
        document?.querySelector('#search')?.focus();
      }
    }

    Array.from(options).forEach(option => {
      option.addEventListener('click', onClick, true);
    });

    // eslint-disable-next-line consistent-return
    return () =>
      Array.from(options).forEach(option =>
        option?.removeEventListener('click', onClick, true),
      );
  }, [process]);

  function onChange(evt: SyntheticInputEvent<>) {
    const { value } = evt.target;
    // The create process is done through the domain/suggest page
    // instead we could add a redirect to the routes
    const page = value === 'create' ? 'suggest' : value;

    navigate(`/domain/${page}/${cartId}`);
  }

  return (
    <>
      <Hide from="sm">
        <SrOnly>
          <Label target="domain-process-nav-switch" text={label} />
        </SrOnly>
        <Select
          id="domain-process-nav-switch"
          value={process}
          values={values}
          onChange={onChange}
          variant={['block']}
        />
      </Hide>
      <Hide until="sm">
        <div>
          <SegmentedControl
            legend={label}
            name="domain-process-nav-switch"
            value={process}
            onChange={onChange}
          >
            {items.map(({ icon, value, text }) => (
              <SegmentedControlItem value={value} key={value}>
                {icon}
                {text}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </div>
      </Hide>
    </>
  );
}

export default (DomainProcessNavSwitch: React.ComponentType<Props>);
